/**
 * Cifras mágicas del tracker: regalos, cartas, duendes, kilómetros…
 *
 * Todo es **determinista**: función pura del instante. Ni un `Math.random()`.
 * Dos razones, y las dos importan:
 *
 * 1. El servidor pinta el HTML indexable y el cliente lo rehidrata; si las
 *    cifras no coincidieran, React descartaría el árbol entero.
 * 2. Una cifra que salta al azar cada segundo no se lee como magia, se lee como
 *    error. Los contadores de preparación **solo suben**, y el "ruido" que los
 *    hace creíbles sale de una función hash de la hora, no de un dado.
 */

import { CUMULATIVE_COUNTRIES, ROUTE, WORLD_COUNTRIES } from './route'
import type { MagicStats, TrackerPhase, TrackerState } from './types'

/** Regalos que salen del taller cada Navidad. */
export const TOTAL_GIFTS = 2_147_483_647

/** Kilómetros del recorrido completo. */
export const TOTAL_KILOMETERS = 510_000_000

/** Cartas que llegan al Polo Norte en una campaña. */
export const TOTAL_LETTERS = 34_500_000

/** Duendes en plantilla cuando el taller está a pleno rendimiento. */
export const MAX_ELVES = 180_000

/** Renos del tiro, Rudolph incluido. */
export const TOTAL_REINDEER = 9

/** Hogares que esperan al trineo. */
export const TOTAL_HOMES = 526_000_000

const DAY_MS = 86_400_000

/**
 * Hash entero → [0, 1). Variante de mulberry32 sin estado: da un número
 * estable para cada semilla, que es justo lo que necesitamos para que el
 * "ruido" de las cifras sea siempre el mismo a la misma hora.
 */
export function hashUnit(seed: number): number {
  let value = (seed | 0) + 0x6d2b79f5
  value = Math.imul(value ^ (value >>> 15), value | 1)
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
  return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * Avance del año de taller: 0 justo después de la Navidad anterior, 1 al
 * despegar. Es lo que hace que en enero apenas haya regalos y en diciembre el
 * taller esté desbordado.
 */
export function workshopProgress(state: TrackerState): number {
  const seasonStartMs = Date.UTC(state.season.year - 1, 11, 26)
  const span = state.season.tripStartMs - seasonStartMs
  return clamp01((state.nowMs - seasonStartMs) / span)
}

/** Curva de taller: arranca despacio y se dispara en el último tramo. */
function workshopCurve(progress: number): number {
  return Math.pow(progress, 2.2)
}

/**
 * Ruido creíble alrededor de un valor: ±`spread`, estable dentro del mismo
 * minuto. Se aplica solo a magnitudes que de verdad fluctúan (duendes de
 * turno), nunca a los acumulados, que deben crecer siempre.
 */
function wobble(value: number, minuteSeed: number, spread: number): number {
  return Math.round(value * (1 + (hashUnit(minuteSeed) - 0.5) * 2 * spread))
}

/** Duendes de turno: el taller no para, pero de madrugada hay menos gente. */
function elvesOnShift(state: TrackerState, progress: number): number {
  const hour = new Date(state.nowMs).getUTCHours()
  // Dos picos al día (turno de mañana y de tarde), suelo del 45 % de noche.
  const shift = 0.45 + 0.55 * ((1 + Math.sin(((hour - 4) / 24) * Math.PI * 2)) / 2)
  const base = MAX_ELVES * (0.12 + 0.88 * workshopCurve(progress)) * shift
  return Math.max(120, wobble(base, Math.floor(state.nowMs / 60_000), 0.015))
}

/**
 * Minutos que el trineo lleva volando (o voló, si ya terminó). Se muestra como
 * duración del viaje.
 */
function travelMinutes(state: TrackerState): number {
  if (state.phase === 'completed') {
    return Math.round((state.season.tripEndMs - state.season.tripStartMs) / 60_000)
  }
  if (state.phase !== 'traveling') return 0
  return Math.max(0, Math.round((state.nowMs - state.season.tripStartMs) / 60_000))
}

/** Todas las cifras de un instante. */
export function computeMagicStats(state: TrackerState): MagicStats {
  const progress = workshopProgress(state)
  const curve = workshopCurve(progress)
  const traveling = state.phase === 'traveling' || state.phase === 'completed'
  const routeProgress = state.progress

  const giftsReady = traveling ? TOTAL_GIFTS : Math.floor(TOTAL_GIFTS * curve)
  const giftsDelivered = traveling ? Math.floor(TOTAL_GIFTS * routeProgress) : 0

  return {
    giftsReady,
    giftsDelivered,
    // Las cartas llegan antes que los regalos salen: curva más suave.
    lettersReceived: Math.floor(TOTAL_LETTERS * Math.pow(progress, 1.4)),
    elvesWorking: elvesOnShift(state, progress),
    // Los nueve renos se van incorporando al entrenamiento a lo largo del año.
    reindeerTraining: Math.min(TOTAL_REINDEER, 1 + Math.floor(TOTAL_REINDEER * curve)),
    kilometers: traveling
      ? Math.floor(TOTAL_KILOMETERS * routeProgress)
      : TOTAL_KILOMETERS,
    citiesVisited: Math.max(0, Math.min(state.stopIndex, ROUTE.length - 2)),
    countriesVisited: state.phase === 'completed' ? WORLD_COUNTRIES : state.countriesVisited,
    homesWaiting: traveling
      ? Math.max(0, Math.floor(TOTAL_HOMES * (1 - routeProgress)))
      : TOTAL_HOMES,
    travelMinutes: travelMinutes(state),
  }
}

/**
 * Índice rotatorio y determinista sobre una lista: cambia cada `slotHours`
 * horas y no se repite dos días seguidos a la misma hora.
 */
export function rotatingIndex(nowMs: number, length: number, slotHours = 3): number {
  if (length <= 0) return 0
  const slot = Math.floor(nowMs / (slotHours * 3_600_000))
  return Math.floor(hashUnit(slot) * length) % length
}

/**
 * Cubo de cercanía a la Navidad. Decide qué familia de mensajes se muestra en
 * la fase de preparativos: no es lo mismo "queda medio año" que "queda un día".
 */
export type ProximityBucket = 'far' | 'months' | 'weeks' | 'days' | 'hours'

export function proximityBucket(state: TrackerState): ProximityBucket {
  const days = (state.season.tripStartMs - state.nowMs) / DAY_MS
  if (days > 120) return 'far'
  if (days > 30) return 'months'
  if (days > 7) return 'weeks'
  if (days > 1) return 'days'
  return 'hours'
}

/** Qué cifras se destacan en cada fase (el resto siguen disponibles). */
export const HIGHLIGHTED_STATS: Record<TrackerPhase, readonly (keyof MagicStats)[]> = {
  preparing: ['giftsReady', 'lettersReceived', 'elvesWorking', 'reindeerTraining'],
  liftoff: ['giftsReady', 'elvesWorking', 'reindeerTraining', 'homesWaiting'],
  traveling: ['giftsDelivered', 'kilometers', 'citiesVisited', 'countriesVisited'],
  completed: ['giftsDelivered', 'countriesVisited', 'kilometers', 'travelMinutes'],
}

/** Total de países del recorrido, para el copy de «misión cumplida». */
export const FINAL_COUNTRIES = CUMULATIVE_COUNTRIES[CUMULATIVE_COUNTRIES.length - 1]
